/**
 * Bearbottom — Shipping Countdown
 *
 * Replaces "In stock, ready to ship" with a live countdown to the daily
 * 12:00 PM ET shipping cutoff (Tampa time). Stacks with low-stock urgency
 * so messages reinforce instead of overwriting each other.
 *
 * State matrix (applies identically to PDPs and the bundle builder):
 *   Healthy + weekday before noon ET (non-holiday) → "Order in Xh Ym to ship today"
 *   Healthy + after noon, weekend, or holiday     → "In stock, ready to ship"
 *   Low stock + countdown applicable              → low-stock primary line + countdown line stacked
 *   Low stock + no countdown (post-cutoff, etc.)  → leave the underlying script's text
 *   Out of stock                                  → leave alone
 *
 * Holidays auto-computed each year. Standard 6 carrier holidays:
 *   New Year's Day, Memorial Day, Independence Day, Labor Day,
 *   Thanksgiving, Christmas Day
 * Federal observance applied: Saturday → Friday, Sunday → Monday.
 *
 * Targets two surfaces:
 *   PDPs:           [data-product-inventory] inside snippets/product-inventory.liquid
 *   Bundle builder: [data-inventory-text]    inside sections/bundle-builder.liquid
 *
 * Stock-state detection is contextual:
 *   PDP    → parent .icon-and-text has 'inventory--low'        (low)
 *           → ancestor li.sales-point has 'hide'                (oos)
 *   Bundle → sibling [data-inventory-dot] has '...dot--low'    (low)
 *           → sibling [data-inventory-dot] has '...dot--out'   (oos)
 *
 * MutationObserver catches variant/color/pack-size changes so the
 * countdown re-applies, stacks, or yields within a single tick.
 */
(function () {
  'use strict';

  if (window.__bbShipCountdownLoaded) return;
  window.__bbShipCountdownLoaded = true;

  var CUTOFF_HOUR     = 12;                                                        // 12:00 PM ET
  var TIMEZONE        = 'America/New_York';
  var SELECTORS       = ['[data-product-inventory]', '[data-inventory-text]'];     // PDP + bundle
  var TICK_MS         = 30000;
  var BADGE_TEXT      = 'In stock, ready to ship';
  var SECONDARY_CLASS = 'bb-ship-countdown';
  var SECONDARY_STYLE = 'display:block;margin-top:2px;font-size:14px;color:#595959;font-weight:400;';

  var holidaysCache = {};

  // ---------- Date helpers ----------

  function pad(n) { return n < 10 ? '0' + n : '' + n; }

  function fmtDate(year, month, day) {
    return year + '-' + pad(month) + '-' + pad(day);
  }

  function nthWeekday(year, month, n, dayOfWeek) {
    var firstOfMonth = new Date(Date.UTC(year, month - 1, 1));
    var firstDayOfWeek = firstOfMonth.getUTCDay();
    var offset = (dayOfWeek - firstDayOfWeek + 7) % 7;
    return 1 + offset + (n - 1) * 7;
  }

  function lastWeekday(year, month, dayOfWeek) {
    var lastOfMonth = new Date(Date.UTC(year, month, 0));
    var lastDay = lastOfMonth.getUTCDate();
    var offset = (lastOfMonth.getUTCDay() - dayOfWeek + 7) % 7;
    return lastDay - offset;
  }

  function computeHolidays(year) {
    var base = [
      fmtDate(year, 1, 1),                            // New Year's Day
      fmtDate(year, 5, lastWeekday(year, 5, 1)),      // Memorial Day (last Mon)
      fmtDate(year, 7, 4),                            // Independence Day
      fmtDate(year, 9, nthWeekday(year, 9, 1, 1)),    // Labor Day (1st Mon)
      fmtDate(year, 11, nthWeekday(year, 11, 4, 4)),  // Thanksgiving (4th Thu)
      fmtDate(year, 12, 25)                           // Christmas Day
    ];
    var set = {};
    for (var i = 0; i < base.length; i++) {
      set[base[i]] = true;
      var d = new Date(base[i] + 'T12:00:00Z');
      var dow = d.getUTCDay();
      if (dow === 6) {
        var fri = new Date(d.getTime() - 86400000);
        set[fmtDate(fri.getUTCFullYear(), fri.getUTCMonth() + 1, fri.getUTCDate())] = true;
      } else if (dow === 0) {
        var mon = new Date(d.getTime() + 86400000);
        set[fmtDate(mon.getUTCFullYear(), mon.getUTCMonth() + 1, mon.getUTCDate())] = true;
      }
    }
    return set;
  }

  function getTampaTime(now) {
    var f = new Intl.DateTimeFormat('en-US', {
      timeZone: TIMEZONE,
      hour12: false,
      weekday: 'short',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
    var p = {};
    f.formatToParts(now).forEach(function (x) {
      if (x.type !== 'literal') p[x.type] = x.value;
    });
    return {
      weekday: p.weekday,
      year:    parseInt(p.year, 10),
      month:   parseInt(p.month, 10),
      day:     parseInt(p.day, 10),
      hour:    parseInt(p.hour, 10),
      minute:  parseInt(p.minute, 10),
      second:  parseInt(p.second, 10)
    };
  }

  function isShippingDay(t) {
    if (t.weekday === 'Sat' || t.weekday === 'Sun') return false;
    if (!holidaysCache[t.year]) holidaysCache[t.year] = computeHolidays(t.year);
    var todayStr = fmtDate(t.year, t.month, t.day);
    return !holidaysCache[t.year][todayStr];
  }

  function getMode(t) {
    if (!isShippingDay(t)) return 'badge';
    if (t.hour >= CUTOFF_HOUR) return 'badge';
    return 'countdown';
  }

  function buildCountdown(t) {
    var remaining = ((CUTOFF_HOUR - t.hour - 1) * 3600)
                  + ((60 - t.minute - 1) * 60)
                  + (60 - t.second);
    var h = Math.floor(remaining / 3600);
    var m = Math.floor((remaining % 3600) / 60);
    var time = (h > 0 ? h + 'h ' : '') + m + 'm';
    return 'Order in ' + time + ' to ship today';
  }

  // ---------- DOM helpers ----------

  /**
   * Returns 'healthy' | 'low' | 'oos' for either PDP or bundle context.
   */
  function getStockState(el) {
    // PDP: low-stock signaled on parent .icon-and-text
    var iconAndText = el.parentElement;
    if (iconAndText && iconAndText.classList && iconAndText.classList.contains('inventory--low')) {
      return 'low';
    }
    // PDP: OOS signaled by 'hide' on the wrapping li
    var pdpLi = el.closest ? el.closest('li.sales-point') : null;
    if (pdpLi && pdpLi.classList.contains('hide')) {
      return 'oos';
    }

    // Bundle: state signaled by sibling dot's modifier class
    var bundleStatus = el.closest ? el.closest('[data-inventory-status]') : null;
    if (bundleStatus) {
      var dot = bundleStatus.querySelector('[data-inventory-dot]');
      if (dot && dot.classList) {
        if (dot.classList.contains('bundle-inventory__dot--out')) return 'oos';
        if (dot.classList.contains('bundle-inventory__dot--low')) return 'low';
      }
    }

    return 'healthy';
  }

  /**
   * Returns the appropriate ancestor to observe for state changes.
   * PDP    → the wrapping li.sales-point
   * Bundle → the [data-inventory-status] container
   */
  function getObserveTarget(el) {
    if (!el.closest) return el.parentElement || el;
    return el.closest('li.sales-point')
        || el.closest('[data-inventory-status]')
        || el.parentElement
        || el;
  }

  function getPrimaryText(el) {
    var fc = el.firstChild;
    if (fc && fc.nodeType === 3) return fc.textContent.trim();
    return el.textContent.trim();
  }

  function escapeHTML(s) {
    var d = document.createElement('div');
    d.textContent = s;
    return d.innerHTML;
  }

  // ---------- Apply logic ----------

  function applyTo(el, t) {
    var stockState = getStockState(el);
    if (stockState === 'oos') return;

    var mode = getMode(t);

    if (stockState === 'low') {
      var existing = el.querySelector('.' + SECONDARY_CLASS);

      if (mode === 'badge') {
        // No countdown to add. Clean up any prior stack.
        if (existing) {
          el.textContent = getPrimaryText(el);
        }
        return;
      }

      // Stacked: low-stock primary + countdown secondary
      var countdown = buildCountdown(t);
      if (existing) {
        if (existing.textContent !== countdown) existing.textContent = countdown;
      } else {
        var primary = getPrimaryText(el);
        el.innerHTML = escapeHTML(primary)
                     + '<span class="' + SECONDARY_CLASS + '" style="' + SECONDARY_STYLE + '">'
                     + escapeHTML(countdown)
                     + '</span>';
      }
      return;
    }

    // Healthy: single-line countdown or static badge
    var msg = (mode === 'countdown') ? buildCountdown(t) : BADGE_TEXT;
    if (el.textContent !== msg) el.textContent = msg;
  }

  function update() {
    var t = getTampaTime(new Date());
    var els = document.querySelectorAll(SELECTORS.join(','));
    for (var i = 0; i < els.length; i++) {
      applyTo(els[i], t);
    }
  }

  function setupObservers() {
    var els = document.querySelectorAll(SELECTORS.join(','));
    for (var i = 0; i < els.length; i++) {
      var target = getObserveTarget(els[i]);
      if (!target) continue;
      var obs = new MutationObserver(function () { update(); });
      obs.observe(target, {
        attributes: true,
        attributeFilter: ['class'],
        subtree: true,
        characterData: true,
        childList: true
      });
    }
  }

  function init() {
    update();
    setupObservers();
    setInterval(update, TICK_MS);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
